import type { ComponentProps } from 'react';

import {
  standOutProductId,
  premiumProductId,
  brandedProductId,
  premiumAsiaProductId,
  brandedBasicTiltProductId,
  premiumTiltProductId,
} from 'src/app/shared/constants/productIds';
import {
  PREMIUM_ASIA_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
  PREMIUM_TILT_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
  PREMIUM_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
  type PremiumUpsellStorageKey,
} from 'src/app/shared/constants/storage';
import type { PremiumUpsell } from 'src/app/v2/components/PremiumUpsell';
import type { useGetOrderSummary } from 'src/app/v2/hooks/useGetOrderSummary';
import type { Cart, HirerAccount } from 'src/graphql/generated';

export type PremiumUpsellType = {
  sessionStorageKey: PremiumUpsellStorageKey;
  baseProductId: string;
  upsellProductId: string;
};

type shouldShowUpsellProps = {
  job: ComponentProps<typeof PremiumUpsell>['job'];
  hirerAccount: HirerAccount | null;
  orderSummary: ReturnType<typeof useGetOrderSummary>;
  cartState?: Cart;
  upsellProduct: PremiumUpsellType;
};

const PREMIUM_UPSELL_PRODUCT_IDS_MAPPING: Record<string, PremiumUpsellType> = {
  PremiumUpsell: {
    sessionStorageKey: PREMIUM_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
    baseProductId: standOutProductId,
    upsellProductId: premiumProductId,
  },
  PremiumAsiaUpsell: {
    sessionStorageKey: PREMIUM_ASIA_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
    baseProductId: brandedProductId,
    upsellProductId: premiumAsiaProductId,
  },
  PremiumUpsellTilt: {
    sessionStorageKey: PREMIUM_TILT_UPSELL_WIDGET_SHOWN_STORAGE_KEY,
    baseProductId: brandedBasicTiltProductId,
    upsellProductId: premiumTiltProductId,
  },
};

const hasProductInCart = ({
  productId,
  cartState,
}: {
  productId: string;
  cartState?: Cart;
}) =>
  Boolean(cartState?.items.find((item) => productId.includes(item.product.id)));

export const shouldShowPremiumUpsell = ({
  job,
  hirerAccount,
  orderSummary,
  upsellProduct,
}: shouldShowUpsellProps): PremiumUpsellType | undefined => {
  if (
    Boolean(
      !upsellProduct &&
        jobExists(job) &&
        !isFullyDiscountedPricingGroup(hirerAccount, orderSummary),
    )
  ) {
    return undefined;
  }

  return upsellProduct;
};

export const findUpsellProduct = (
  cartState: Cart | undefined,
): PremiumUpsellType | undefined =>
  Object.values(PREMIUM_UPSELL_PRODUCT_IDS_MAPPING).find((upsell) =>
    isUpsellApplicable(upsell, cartState),
  );

const isUpsellApplicable = (
  upsell: PremiumUpsellType,
  cartState?: Cart,
): boolean => {
  const hasSeenUpsell = hasPremiumUpsellWidgetBeenShown(
    upsell.sessionStorageKey,
  );
  const hasBaseProductInCart = hasProductInCart({
    cartState,
    productId: upsell.baseProductId,
  });

  return hasSeenUpsell || hasBaseProductInCart;
};

const hasPremiumUpsellWidgetBeenShown = (storageKey: PremiumUpsellStorageKey) =>
  sessionStorage.getItem(storageKey) === 'true';

const jobExists = (job: ComponentProps<typeof PremiumUpsell>['job']) =>
  Boolean(job);

const isFullyDiscountedPricingGroup = (
  hirerAccount: HirerAccount | null,
  orderSummary: ReturnType<typeof useGetOrderSummary>,
) =>
  hirerAccount?.contractsSummary?.hasAnyActivePricingGroupContracts &&
  orderSummary?.total.value === 0;
